import { IStop } from "src/components/RegisterRoute";
import moment from "moment";
import { CheckpointStop, CreateAreaReworkPayload } from "@types";

export const formatDateBr = (dateString?: string, showHour = true) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "";

  return showHour
    ? `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
    : date.toLocaleDateString("pt-BR");
};

/**
 * Formata datas para exibição em formato BR (DD/MM/YYYY HH:mm)
 * Usa moment para interpretar corretamente datas locais (sem timezone)
 * Específico para uso em pacotes onde as datas são inseridas pelo usuário
 */
export const formatDateBrLocal = (dateString?: string, showHour = true) => {
  if (!dateString) return "";

  // Usar moment para interpretar a data corretamente
  // Se a string não tem timezone (ex: "2024-01-15T10:00:00"), interpreta como local
  // Se tem "Z" ou offset, converte para local automaticamente
  const date = moment(dateString);

  if (!date.isValid()) return "";

  return showHour ? date.format("DD/MM/YYYY HH:mm") : date.format("DD/MM/YYYY");
};

const STOP_DATETIME_DISPLAY_FORMAT = "DD/MM/YYYY - HH:mm";

const isStopDateTimeField = (title?: string) => {
  const normalized = (title ?? "").toLowerCase();
  return normalized.includes("data") && normalized.includes("hora");
};

/**
 * Formata a data/hora em que a parada do checklist foi registrada.
 * Só exibe data quando a parada foi de fato preenchida (campo "data/hora" do step ou stopFilledAt).
 * Caso contrário retorna "---" — não usar createdAt/updatedAt, que mostrariam a data de criação.
 */
export const formatCheckpointStopDateTime = (checkpointStop?: CheckpointStop | null): string => {
  if (!checkpointStop) return "---";

  for (const step of checkpointStop.steps ?? []) {
    for (const field of step.fields ?? []) {
      if (isStopDateTimeField(field.field?.title) && field.actualValue) {
        return field.actualValue;
      }
    }
  }

  if (checkpointStop.stopFilledAt) {
    return moment.utc(checkpointStop.stopFilledAt).local().format(STOP_DATETIME_DISPLAY_FORMAT);
  }

  // Checklist ainda não preenchido -> não exibir data/hora.
  return "---";
};

export const formatDateBrUtc = (dateStr?: string | null) => {
  if (!dateStr) return "";
  const trimmed = dateStr.slice(0, 23) + "Z";
  return moment.utc(trimmed).local().format("DD/MM/YYYY HH:mm");
};

export const formatDateUTCBr = (dateString?: string | null, showHour = true) => {
  if (!dateString) return "";
  const trimmed = dateString.slice(0, 23) + "Z";
  const m = moment.utc(trimmed).local();
  return m.format(showHour ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

export const validarDate = (dateString: string, showHour = true) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const today = new Date();

  date.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (date < today) {
    return false;
  }

  return true;
};

export const formatTimeBr = (date: Date, type?: "DEFAULT" | "HOUR" | "MINUTES" | "SECONDS" | "HOUR_AND_MINUTES") => {
  if (!date) return "";

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  switch (type) {
    case "HOUR":
      return hours;
    case "MINUTES":
      return minutes;
    case "SECONDS":
      return seconds;
    case "HOUR_AND_MINUTES":
      return `${hours}:${minutes}`;
    default:
      return `${hours}:${minutes}:${seconds}`;
  }
};

export const formatDateEn = (dateString?: string) => {
  if (!dateString) return "";
  const [day, month, year] = dateString.split("/");
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export const safeFormatDateEn = (dateInput: string | Date | undefined): string => {
  if (!dateInput) return "";

  // Se for do tipo Date ou string completa (como "Wed Jun 18...")
  const isFullDateString = typeof dateInput === "string" && !dateInput.includes("/");

  try {
    const date = isFullDateString ? new Date(dateInput) : moment(dateInput, "DD/MM/YYYY").toDate();
    return formatDateEn(moment(date).format("DD/MM/YYYY"));
  } catch (err) {
    console.warn("[safeFormatDateEn] erro ao formatar data", err);
    return "";
  }
};

export const formatValueKmMask = (value: number) => {
  if (value === null || value === undefined) return "";

  const valueKM = value / 1000;

  const valueFormatted = valueKM.toFixed(0);

  return valueKM >= 1 ? `${valueFormatted}km` : `${valueKM * 1000}m`;
};

export const formatarMeters = (VALUE: string) => {
  if (!VALUE) return "";
  let formattedValue = VALUE?.replace(/\D/g, "");
  formattedValue = formattedValue?.replace(/(\d)(\d{2})$/, "$1,$2");

  return formattedValue;
};

export const formatMetersMaskInNumber = (VALUE: string, type: "DEFAULT" | "T" = "DEFAULT") => {
  if (!VALUE) return "";

  const TYPE = {
    DEFAULT: 100,
    T: 1000,
  };

  let formattedValue = VALUE?.replace(/\./g, "");
  formattedValue = formattedValue?.replace(",", ".");
  const v = parseFloat(formattedValue) * TYPE[type];

  return parseInt(v.toString());
};

export const formatKg = (value: string) => {
  if (!value) return "";

  value = value.replace(/\D/g, "");
  value = (Number(value) / 100).toFixed(2);
  return value.replace(".", ",");
};

export const formatMoney = (value: string) => {
  if (!value) return "";

  value = value.replace(/\D/g, "");
  value = (Number(value) / 100).toFixed(2);

  const [integer, decimal] = value.split(".");
  const integerFormatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${integerFormatted},${decimal}`;
};

export const formatMoneyInNumber = (value: string) => {
  return parseFloat(value?.replace(/\./g, "").replace(",", "."));
};

export const formatMaskTime = (value: string) => {
  // Remove tudo que não seja número
  let time = value.replace(/\D/g, "");

  // Adiciona dois pontos entre as horas e os minutos
  if (time.length >= 3) {
    time = `${time.slice(0, 2)}:${time.slice(2, 4)}`;
  } else if (time.length >= 3) {
    time = `${time.slice(0, 2)}:${time.slice(2)}`;
  }

  return time;
};

export const kmToMeters = (km?: string) => {
  if (!km) return 0;
  const calc = parseFloat(km) * 1000;
  return calc.toLocaleString();
};

export const formaTimeDifference = (dataEnvio: string) => {
  if (!dataEnvio) {
    return "";
  }

  const data = moment(dataEnvio);
  const hoje = moment();
  return data.isSame(hoje, "day") ? `${data.format("HH:mm")}` : hoje.diff(data, "days").toString() + " d";
};

export const formatMinutesToBrHour = (minutes: number | null | undefined) => {
  if (!minutes || minutes < 0) return "00h00min";
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}h${m}min`;
};

export const formatNamesGroup = (displayName: string) => {
  const names = displayName
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);

  // Remove duplicados preservando a ordem
  const uniqueNames = [...new Set(names)];

  const firstFour = uniqueNames.slice(0, 4);
  const remaining = uniqueNames.length - 4;

  if (remaining > 0) {
    return `${firstFour.join(", ")} +${remaining}`;
  }

  return firstFour.join(", ");
};

export const formatIsoWithoutT = (isoDate: string) => {
  return moment(isoDate).format("DD/MM/YYYY HH:mm");
};

export const convertBrHourToMinutes = (value: string): number => {
  if (!value) return 0;

  const horasMatch = value.match(/(\d+)h/);
  const minutosMatch = value.match(/(\d+)min/);

  const horas = horasMatch ? parseInt(horasMatch[1]) : 0;
  const minutos = minutosMatch ? parseInt(minutosMatch[1]) : 0;

  return horas * 60 + minutos;
};

export const DICT = {
  REFUELING: "Abastecimento",
  LOADING: "Carregamento",
  DEALERSHIP: "Concessionária",
  REST: "Descanso",
  UNLOADING: "Descarregamento",
  END_WORKDAY: "Fim de Jornada de Trabalho",
  WEIGHING: "Pesagem",
  PASSING_POINT: "Ponto de Passagem",
  POLICE_CHECK: "Posto PRF/ANTT",
  MEAL: "Refeição",
  MECHANICAL_EMERGENCY: "Socorro Mecânico",
  ORIGIN: 'Origem',
  DESTINY: 'Destino',
  OTHERS: 'Outros'
};
export const formatCategory = (text: string) => {
  return DICT[text as keyof typeof DICT] ?? "";
};

export const formatLocalDate = (dateString?: string, formatDate?: string) => {
  if (!dateString) return;

  return formatDate ? moment.utc(dateString).local().format(formatDate) : moment.utc(dateString).local();
};

export const parseIsoToBrDate = (isoDate?: string): string => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

export const formatStayTime = (stayTime: number) => {
  if (!stayTime) return "00:00";

  return moment().startOf("day").add(stayTime, "minutes").format("HH:mm");
};

export const forceHHMM = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 4) {
    value = value.slice(0, 4);
  }

  let hour = value.slice(0, 2).padStart(2, "0");
  let minute = value.slice(2, 4).padStart(2, "0");
  if (parseInt(hour) > 23) {
    hour = "23";
  }
  if (parseInt(minute) > 59) {
    minute = "59";
  }
  return `${hour}:${minute}`;
};

export const formatDurationToMinutes = (duration: string) => {
  if (!duration) {
    return 0;
  }

  const regex = /(\d{1,2})h(\d{1,2})min/;
  const matches = duration.match(regex);

  if (matches) {
    const hours = parseInt(matches[1], 10);
    const minutes = parseInt(matches[2], 10);
    const totalMinutes = hours * 60 + minutes;

    return totalMinutes;
  } else {
    return 0;
  }
};

export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "R$ 0,00";
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export const checkEcqualField = (
  field?: string | boolean | number | null,
  fieldState?: string | boolean | number | null
) => {
  return field === fieldState;
};

export const checkEcqualStops = (arr1: IStop[], arr2: IStop[]) => {
  const ids1 = arr1.map((i) => i.position.id).sort((a: string, b: string) => a?.localeCompare(b));
  const ids2 = arr2.map((i) => i.position.id).sort((a: string, b: string) => a?.localeCompare(b));

  return JSON.stringify(ids1) === JSON.stringify(ids2);
};

export const convertMetersToKm = (valueInMeters: number) => {
  if (valueInMeters < 1000) {
    return `${valueInMeters} M`;
  } else {
    const valueInKm = (valueInMeters / 1000).toFixed(2);
    return `${valueInKm} Km`;
  }
};

export const parseDistanceToKm = (distance?: string | number): number => {
  if (distance === null || distance === undefined || distance === "") {
    return 0;
  }

  if (typeof distance === "number") {
    return isNaN(distance) ? 0 : distance;
  }

  const value = parseFloat(distance.replace(",", "."));

  if (isNaN(value)) {
    return 0;
  }

  const isMeters = /\bm\b/i.test(distance) && !/\bkm\b/i.test(distance);

  return isMeters ? value / 1000 : value;
};

export const formatDateInUtc = (date: string) => {
  return moment(date).utc().format();
};

export const formatStayTimeToSeconds = (stayTime: string) => {
  if (!stayTime) {
    return 0;
  }

  const duration = moment.duration(stayTime);
  const totalSeconds = duration.asSeconds();
  return totalSeconds;
};

export const formatPolygon = (
  points: {
    lat: number;
    lng: number;
  }[]
) => {
  return `polygon:${points.map((p) => `${p.lat},${p.lng}`).join(";")}`;
};

export const formatCircle = (
  center: { lat: number; lng: number },
  offset: { lat: number; lng: number },
  radius: number
) => {
  return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
};

export const formatAreaIds = (areas?: CreateAreaReworkPayload[]) => {
  return areas
    ?.map((item) => item.id)
    ?.sort()
    ?.join(",");
};

export const formatStopTime = (timeStr: string): string => {
  if (!timeStr || timeStr.trim() === "") return "00:00";

  const trimmed = timeStr.trim();

  // Se já está no formato HH:MM, retorna como está
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Se está no formato HhMMmin, converte para HH:MM
  const match = trimmed.match(/^(\d+)h(\d+)min$/);
  if (match) {
    const hours = match[1].padStart(2, "0");
    const minutes = match[2].padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  // Se não está em nenhum formato válido, retorna 00:00
  return "00:00";
};

export const formatMoneyInput = (value: string, maxDigits: number = 6): string => {

  let cleanValue = value.replace("R$ ", "").replace("R$", "").replace(/\D/g, "");
  
  if (cleanValue === "") {
    return "0,00";
  }

  if (cleanValue.length > maxDigits) {
    cleanValue = cleanValue.slice(0, maxDigits);
  }

  cleanValue = cleanValue.replace(/^0+/, "") || "0";

  cleanValue = cleanValue.padStart(3, "0");

  const integerPart = cleanValue.slice(0, -2) || "0";
  const decimalPart = cleanValue.slice(-2);
  
  return `${integerPart},${decimalPart}`;
};

export function formatTime(seconds?: number): string {
  if (!seconds || seconds <= 0) {
    return '0min';
  }

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  let result = '';
  if (h > 0) result += `${h}h`;
  if (m > 0) result += `${m}m`;
  if (s > 0 || result === '') result += `${s}s`;

  return result;
}

export function formatTimeFromMinutes(minutes?: number): string {
  if (!minutes || minutes <= 0) {
    return '0min';
  }

  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  let result = '';
  if (h > 0) result += `${h}h`;
  if (m > 0 || result === '') result += `${m}min`;

  return result;
}

 export const formatTitleStrategy = (strategy: string) => {
  const types = {
    lowest: 'Rota com menor consumo energético',
    fastest: 'Rota mais rápida',
    cheapest: 'Rota mais econômica',
    lowest_no_ia: 'Rota com menor consumo energético otimizada',
    lowest_notoll: 'Rota com menor consumo energético (Sem Pedágio)',
    fastest_notoll: 'Rota mais rápida (Sem Pedágio)',
    cheapest_notoll: 'Rota mais econômica (Sem Pedágio)'
  };

  return types[strategy as keyof typeof types] ?? strategy;
};

export const formatDescStrategy = (strategy: string): string => {
  const descriptions = {
    lowest: "Foi levado em consideração as condições atuais de trânsito e o planejamento de doca junto ao embarcador para otimizar o consumo energético.",
    lowest_notoll: "Foi levado em consideração as condições atuais de trânsito e o planejamento de doca junto ao embarcador para otimizar o consumo energético sem pedágio.",
    fastest: "Foi levado em consideração as condições atuais de trânsito para encontrar a rota mais rápida disponível.",
    fastest_notoll: 'Foi levado em consideração as condições atuais de trânsito para encontrar a rota mais rápida disponível sem pedágio.',
    cheapest: "Foi levado em consideração as condições atuais de trânsito para encontrar a rota mais econômica.",
    cheapest_notoll: "Foi levado em consideração as condições atuais de trânsito para encontrar a rota mais econômica sem pedágio.",
  };

  return descriptions[strategy as keyof typeof descriptions] || "";
};

export const formatTimeToSeconds = (time?: string) => {
  const regex = /(?:(\d+)h)?(?:(\d+)min)?/i;
  
  if (!time) return 0;

  const match = RegExp(regex).exec(time);

  if (!match) return 0;

  const horas = parseInt(match[1] || "0", 10);
  const minutos = parseInt(match[2] || "0", 10);

  return (horas * 3600) + (minutos * 60);
}